import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/AggiornaGenitoreLog.css";

const API_URL = process.env.REACT_APP_API_URL;

async function readErrorResponse(res, fallbackMessage) {
  try {
    const text = await res.text();
    return text
      ? `${fallbackMessage}: ${res.status} - ${text}`
      : `${fallbackMessage}: ${res.status}`;
  } catch {
    return `${fallbackMessage}: ${res.status}`;
  }
}

const campiMaiuscoli = ["nome", "cognome", "indirizzo"];

const normalizeUpper = (value) => (value || "").toString().toUpperCase();
const normalizePlain = (value) => (value || "").toString();

export default function AggGenitore() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [genitore, setGenitore] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formEdit, setFormEdit] = useState({});

  useEffect(() => {
    let mounted = true;

    async function fetchProfilo() {
      setLoading(true);

      try {
        const res = await authFetch(`${API_URL}/api/genitore/profilo`);

        if (!res.ok) {
          throw new Error(
            await readErrorResponse(res, "Errore nella lettura dei dati")
          );
        }

        const data = await res.json();
        const obj = {
          ...data,
          nome: data.nome || "",
          cognome: data.cognome || "",
          telefono: data.telefono || "",
          indirizzo: data.indirizzo || "",
          username: data.email || ""
        };

        if (mounted) {
          setGenitore(obj);
          setFormEdit(obj);
        }
      } catch (err) {
        console.error("Errore caricamento profilo genitore:", err);
        toast.error("Errore nella lettura dei dati");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchProfilo();

    return () => {
      mounted = false;
    };
  }, [authFetch]);

  function handleEditChange(e) {
    const { name, value } = e.target;

    if (campiMaiuscoli.includes(name)) {
      setFormEdit((prev) => ({
        ...prev,
        [name]: normalizeUpper(value)
      }));
      return;
    }

    if (name === "telefono") {
      setFormEdit((prev) => ({
        ...prev,
        [name]: normalizePlain(value)
      }));
      return;
    }

    setFormEdit((prev) => ({ ...prev, [name]: value }));
  }

  function handleCancel() {
    setFormEdit(genitore || {});
    setEditMode(false);
  }

  async function handleSave() {
    if (!formEdit.nome?.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }

    if (!formEdit.cognome?.trim()) {
      toast.error("Il cognome è obbligatorio");
      return;
    }

    if (!formEdit.indirizzo?.trim()) {
      toast.error("L'indirizzo è obbligatorio");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formEdit,
        nome: formEdit.nome.trim().toUpperCase(),
        cognome: formEdit.cognome.trim().toUpperCase(),
        telefono: formEdit.telefono?.trim() || null,
        indirizzo: formEdit.indirizzo.trim().toUpperCase()
      };

      const res = await authFetch(`${API_URL}/api/genitore/profilo`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(
          await readErrorResponse(res, "Errore durante il salvataggio")
        );
      }

      const aggiornato = await res.json();
      const obj = {
        ...aggiornato,
        nome: aggiornato.nome || "",
        cognome: aggiornato.cognome || "",
        telefono: aggiornato.telefono || "",
        indirizzo: aggiornato.indirizzo || "",
        username: aggiornato.email || ""
      };

      setGenitore(obj);
      setFormEdit(obj);
      setEditMode(false);
      toast.success("Profilo aggiornato correttamente");
    } catch (err) {
      console.error("Errore salvataggio profilo genitore:", err);
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p>Caricamento...</p>;
  }

  if (!genitore) {
    return <p>Impossibile caricare i dati del profilo.</p>;
  }

  return (
    <div className="agggenlog-form-container" translate="no">
      <h2 className="agggenlog-form-title">I Miei Dati</h2>

      {!editMode && (
        <div className="agggenlog-dati-griglia">
          <div className="agggenlog-campo">
            <label>Nome</label>
            <div className="agggenlog-valore">{genitore.nome || "-"}</div>
          </div>

          <div className="agggenlog-campo">
            <label>Cognome</label>
            <div className="agggenlog-valore">{genitore.cognome || "-"}</div>
          </div>

          <div className="agggenlog-campo">
            <label>Telefono</label>
            <div className="agggenlog-valore">{genitore.telefono || "-"}</div>
          </div>

          <div className="agggenlog-campo">
            <label>Email</label>
            <div className="agggenlog-valore">{genitore.email || "-"}</div>
          </div>

          <div className="agggenlog-campo">
            <label>Indirizzo</label>
            <div className="agggenlog-valore">{genitore.indirizzo || "-"}</div>
          </div>

          <div className="agggenlog-campo">
            <label>Username</label>
            <div className="agggenlog-valore">{genitore.username || "-"}</div>
          </div>

          <div className="agggenlog-campo">
            <label>Categoria Figlio</label>
            <div className="agggenlog-valore">
              {genitore.nomeCategoriaFiglio || "-"}
            </div>
          </div>

          <div className="agggenlog-campo">
            <label>Stato</label>
            <div className="agggenlog-valore">{genitore.stato || "-"}</div>
          </div>

          <div className="agggenlog-bottoni">
            <button
              type="button"
              onClick={() => setEditMode(true)}
              className="agggenlog-modifica"
            >
              Modifica
            </button>
          </div>
        </div>
      )}

      {editMode && (
        <div className="agggenlog-espansione-griglia">
          <div className="agggenlog-campo">
            <label>Nome</label>
            <input
              className="agggenlog-input uppercase"
              name="nome"
              value={formEdit.nome || ""}
              onChange={handleEditChange}
              disabled={saving}
            />
          </div>

          <div className="agggenlog-campo">
            <label>Cognome</label>
            <input
              className="agggenlog-input uppercase"
              name="cognome"
              value={formEdit.cognome || ""}
              onChange={handleEditChange}
              disabled={saving}
            />
          </div>

          <div className="agggenlog-campo">
            <label>Telefono</label>
            <input
              className="agggenlog-input"
              name="telefono"
              value={formEdit.telefono || ""}
              onChange={handleEditChange}
              disabled={saving}
            />
          </div>

          <div className="agggenlog-campo">
            <label>Indirizzo</label>
            <input
              className="agggenlog-input uppercase"
              name="indirizzo"
              value={formEdit.indirizzo || ""}
              onChange={handleEditChange}
              disabled={saving}
            />
          </div>

          <div className="agggenlog-campo">
            <label>Email</label>
            <input
              className="agggenlog-input"
              value={formEdit.email || ""}
              readOnly
            />
          </div>

          <div className="agggenlog-campo">
            <label>Username</label>
            <input
              className="agggenlog-input"
              value={formEdit.username || ""}
              readOnly
            />
          </div>

          <div className="agggenlog-campo">
            <label>Categoria Figlio</label>
            <input
              className="agggenlog-input"
              value={formEdit.nomeCategoriaFiglio || ""}
              readOnly
            />
          </div>

          <div className="agggenlog-campo">
            <label>Stato</label>
            <input
              className="agggenlog-input"
              value={formEdit.stato || ""}
              readOnly
            />
          </div>

          <div className="agggenlog-bottoni">
            <button
              type="button"
              onClick={handleSave}
              className="agggenlog-salva"
              disabled={saving}
            >
              {saving ? "Salvataggio..." : "Salva"}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="agggenlog-annulla"
              disabled={saving}
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      <div className="agggenlog-footer">
        <button
          type="button"
          className="agggenlog-torna"
          onClick={() => navigate("/gestionale2")}
          disabled={saving}
        >
          Torna al gestionale
        </button>
      </div>
    </div>
  );
}